import {Component, inject, OnInit} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import { AuthenticationService } from '@core/auth/authentication.service';
import {Modal} from '@shared/components/common/modal/modal';
import {Badge, BadgeColor} from '@shared/components/ui/badge/badge';
import {ButtonComponent} from '@shared/components/ui/button/button.component';
import {LabelComponent} from '@shared/components/form/label/label.component';
import {
  AppFormComponent,
  DataTable,
  InputFieldComponent,
  Pagination,
  SearchInput,
  SelectFieldComponent,
  SelectOption,
  TableCellDirective,
  TableColumn,
  TextareaFieldComponent,
} from '@shared/components/ui/list-components';
import {LocationCoordinates} from '@shared/components/form/location-picker/location-picker.component';
import {LocationPickerTriggerComponent} from '@shared/components/form/location-picker/location-picker-trigger.component';
import {coordinateValidator} from '@shared/validators/coordinate.validator';
import {CombosService} from '@shared/services/combos.service';
import {ErrorService} from '@shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {Zona} from '@shared/services/interfaces/combos.interface';
import {Establecimiento} from '../../clientes/clientes.interface';
import {EstablecimientosService} from '../establecimientos.service';
import {AnimalesService} from '../../../animales/animales.service';
import {Animal} from '../../../animales/animales.interface';

export const OBJETIVOS_ESTABLECIMIENTO: SelectOption[] = [
  { value: 'E', label: 'Producción de embriones' },
  { value: 'P', label: 'Preñez' },
  { value: 'X', label: 'Producción de Embriones/Preñez' },
];

export interface EstablecimientoModalData {
  clienteId: number;
  /** Sin establecimiento el modal da de alta uno nuevo. */
  establecimiento?: Establecimiento;
}

/**
 * Ficha del establecimiento desde la ficha del cliente: datos y
 * animales en un solo modal. Los animales son solo para ver. También es el alta:
 * sin establecimiento abre el formulario vacío. Cierra con `true` si se guardó
 * algo, para que el listado se refresque.
 */
@Component({
  selector: 'app-establecimiento-modal',
  imports: [
    HasPermissionDirective,
    Modal,
    Badge,
    ButtonComponent,
    LabelComponent,
    ReactiveFormsModule,
    AppFormComponent,
    InputFieldComponent,
    SelectFieldComponent,
    TextareaFieldComponent,
    LocationPickerTriggerComponent,
    DataTable,
    TableCellDirective,
    Pagination,
    SearchInput,
    DecimalPipe,
  ],
  templateUrl: './establecimiento-modal.html',
})
export class EstablecimientoModal implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  private data: EstablecimientoModalData = inject(MAT_DIALOG_DATA);
  establecimiento: Establecimiento | null = this.data.establecimiento ?? null;

  editando = false;
  guardando = false;
  private cambio = false;
  form!: FormGroup;
  zonas: Zona[] = [];
  objetivoOptions = OBJETIVOS_ESTABLECIMIENTO;


  animales: Animal[] = [];
  cargandoAnimales = false;
  busqueda = '';
  currentPage = 1;
  lastPage = 1;
  perPage = 10;
  total = 0;
  columnasAnimales: TableColumn[] = [
    { key: 'registro_particular', header: 'RP' },
    { key: 'nombre', header: 'Nombre' },
    { key: 'hbp', header: 'HBP' },
    { key: 'tipo', header: 'Categoría' },
    { key: 'tipo_animal', header: 'Rol reproductivo' },
    { key: 'raza', header: 'Raza' },
    { key: 'estado', header: 'Estado' },
  ];

  private fB = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<EstablecimientoModal>);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private establecimientosService = inject(EstablecimientosService);
  private animalesService = inject(AnimalesService);

  get zonasOptions(): SelectOption[] {
    return this.zonas.map(z => ({ value: z.id, label: z.nombre }));
  }

  get objetivoNombre(): string {
    return this.objetivoOptions.find(o => o.value === this.establecimiento?.objetivo)?.label ?? '-';
  }

  /** Partes de `direccion_completa`: zona / ciudad / depto / país. */
  private get partesUbicacion(): string[] {
    return (this.establecimiento?.direccion?.zona?.direccion_completa ?? '').split(' / ');
  }

  get zona(): string {
    return this.partesUbicacion[0] || '-';
  }

  get ciudad(): string {
    return this.partesUbicacion[1] || '-';
  }

  get departamento(): string {
    return this.partesUbicacion[2] || '-';
  }

  get titulo(): string {
    return this.establecimiento?.nombre ?? 'Nuevo establecimiento';
  }

  ngOnInit(): void {
    if (!this.establecimiento) {
      this.editar();
      return;
    }
    this.getAnimales();
  }

  getAnimales(page: number = 1): void {
    if (!this.establecimiento) return;
    this.cargandoAnimales = true;
    this.animalesService.index({
      cli_establecimiento_id: this.establecimiento.id,
      search: this.busqueda || undefined,
      page,
      per_page: this.perPage,
    }).subscribe({
      next: (response) => {
        this.animales = response.data.data;
        this.currentPage = response.data.pagination.current_page;
        this.lastPage = response.data.pagination.last_page;
        this.total = response.data.pagination.total;
      },
      error: (error) => this.errorService.handleError(error),
    }).add(() => this.cargandoAnimales = false);
  }

  onBusqueda(texto: string): void {
    this.busqueda = texto;
    this.getAnimales();
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.getAnimales();
  }

  tipoBadge(tipo: 'V' | 'T'): { label: string; color: BadgeColor } {
    return tipo === 'V' ? { label: 'Vaca', color: 'primary' } : { label: 'Toro', color: 'info' };
  }

  editar(): void {
    const e = this.establecimiento;
    this.form = this.fB.group({
      nombre: [e?.nombre ?? '', Validators.required],
      objetivo: [e?.objetivo ?? '', Validators.required],
      ubi_zona_id: [e?.direccion?.zona?.id ?? '', Validators.required],
      // Number() muestra 353 en vez de "353.00" sin redondear lo guardado
      km: [e?.km != null ? Number(e.km) : '', Validators.min(0)],
      direccion: [e?.direccion?.direccion ?? ''],
      latitud: [e?.direccion?.latitud ?? '', coordinateValidator(-90, 90)],
      longitud: [e?.direccion?.longitud ?? '', coordinateValidator(-180, 180)],
    });
    if (!this.zonas.length) {
      this.combosService.getAllZonasCombo().subscribe({
        next: (response) => this.zonas = response.data,
        error: (error) => this.errorService.handleError(error),
      });
    }
    this.editando = true;
  }

  onCoordinatesChange(coordenadas: LocationCoordinates): void {
    this.form.patchValue(coordenadas);
  }

  guardar(): void {
    if (!this.permissionAuth.hasPermission('clientes', this.establecimiento ? 'edit' : 'create')) return;
    this.guardando = true;
    const valores = this.form.value;
    // app-select emite string: la zona se manda como número
    const payload = { ...valores, ubi_zona_id: Number(valores.ubi_zona_id) };

    if (!this.establecimiento) {
      this.establecimientosService.store({...payload, cli_cliente_id: this.data.clienteId}).subscribe({
        next: () => {
          this.snackbarService.showSnackBar('Establecimiento creado correctamente');
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.guardando = false;
          this.errorService.handleError(error);
        },
      });
      return;
    }

    this.establecimientosService.update(this.establecimiento.id, payload).subscribe({
      next: () => {
        this.snackbarService.showSnackBar('Establecimiento actualizado correctamente');
        this.cambio = true;
        this.recargar();
      },
      error: (error) => {
        this.guardando = false;
        this.errorService.handleError(error);
      },
    });
  }

  /** Cancelar en el alta cierra el modal; en la edición vuelve a la vista. */
  cancelar(): void {
    if (!this.establecimiento) {
      this.cerrar();
      return;
    }
    this.editando = false;
  }

  private recargar(): void {
    if (!this.establecimiento) return;
    this.establecimientosService.show(this.establecimiento.id).subscribe({
      next: (response) => {
        // show() no trae los agregados del listado: se conservan
        const {animales_count, proximo_agendamiento} = this.establecimiento!;
        this.establecimiento = {...response.data, animales_count, proximo_agendamiento};
        this.editando = false;
      },
      error: (error) => this.errorService.handleError(error),
    }).add(() => this.guardando = false);
  }

  cerrar(): void {
    this.dialogRef.close(this.cambio);
  }
}
